/**
 * Blank-line preservation — the cross-platform contract for keeping runs of
 * consecutive blank lines in note content instead of collapsing them to one.
 *
 * Markdown is lossy here: CommonMark folds any run of blank lines into a single
 * block boundary, so both editors (TipTap on web, the block editor on Swift)
 * drop the user's extra spacing on parse. To preserve it we represent each
 * extra blank line as an EMPTY PARAGRAPH block in the editor document, and keep
 * storage as clean canonical markdown (literal `\n` runs, no marker character).
 *
 * The canonical mapping — both platforms MUST agree exactly, or a note edited on
 * one client and synced to another would oscillate its blank-line count on every
 * sync (a phantom-edit loop):
 *
 *   N blank lines between two content blocks
 *     ⟺ (N − 1) empty paragraph blocks
 *     ⟺ (N + 1) `\n` separators
 *
 *   N=1 (normal adjacency)  → 0 empty paragraphs → `A\n\nB`      (unchanged)
 *   N=2                     → 1 empty paragraph  → `A\n\n\nB`
 *   N=3                     → 2 empty paragraphs → `A\n\n\n\nB`
 *
 * Swift mirrors {@link blankParagraphCount} (and the cap) in its block parser.
 * If you change the arithmetic here, change it there too.
 */

/**
 * Transient marker wrapped in private-use-area codepoints (U+E000) so it can
 * never collide with real prose. The wrapping chars are INVISIBLE in most
 * editors and diffs but are load-bearing: a paragraph whose text equals this
 * exact string is emptied by the parse hook, so without the U+E000 wrappers a
 * user typing a literal `caliu-blank-line` line would be silently destroyed.
 * Do NOT "simplify" the value to plain ASCII (a test guards against this). The
 * marker exists ONLY between the string expansion and the editor's parse hook;
 * it is never stored.
 */
export const BLANK_LINE_SENTINEL = "caliu-blank-line"

/**
 * Maximum number of empty paragraph blocks inserted for a single run of blank
 * lines. Guards against a pathological paste of thousands of newlines creating
 * thousands of empty editor nodes. Mirrored on Swift.
 */
export const MAX_BLANK_PARAGRAPHS = 20

/**
 * Map a blank-line count to the number of empty paragraph blocks that represent
 * it, clamped to {@link MAX_BLANK_PARAGRAPHS}. This is the single source of truth
 * for the count both editors must produce.
 */
export function blankParagraphCount(blankLines: number): number {
  return Math.min(Math.max(blankLines - 1, 0), MAX_BLANK_PARAGRAPHS)
}

/**
 * Character ranges that must never be touched: fenced code blocks (```...```)
 * and inline code (`...`). Blank lines inside a fenced block are content and
 * stay verbatim. (Mirror of the fence guard in `tagContent.ts`, minus link
 * destinations, which are single-line and irrelevant here.)
 */
function protectedCodeRanges(content: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = []
  const codeRegex = /```[\s\S]*?```|`[^`]*`/g
  let match: RegExpExecArray | null
  while ((match = codeRegex.exec(content)) !== null) {
    ranges.push([match.index, match.index + match[0].length])
  }
  return ranges
}

/**
 * Expand each run of blank lines in a note BODY (frontmatter already split off)
 * into the canonical `\n\n` separator plus the matching number of sentinel
 * paragraphs, so a markdown parser (markdown-it) emits one paragraph per extra
 * blank line. The editor's parse hook then empties those sentinel paragraphs to
 * real empty paragraphs, and its serializer turns them back into literal blank
 * lines — keeping storage clean.
 *
 * Only INTERIOR runs (between two content blocks) are expanded. A run at the
 * very start or end of the body is left as-is and so collapses on the editor
 * round-trip: leading/trailing blank lines do not survive ProseMirror reliably,
 * and the Swift parser canonicalizes them the same way, so dropping them on both
 * platforms keeps cross-device round-trips stable.
 *
 * Runs inside fenced code blocks are left untouched. A normal single blank line
 * (`\n\n`) never matches `\n{3,}`, so unchanged content stays byte-for-byte.
 */
export function expandBlankLines(body: string, sentinel = BLANK_LINE_SENTINEL): string {
  const ranges = protectedCodeRanges(body)
  return body.replace(/\n{3,}/g, (run: string, offset: number) => {
    if (offset === 0) return run // leading run: canonicalized away on round-trip
    if (offset + run.length >= body.length) return run // trailing run: same
    if (ranges.some(([start, end]) => offset >= start && offset < end)) return run
    // run length k newlines == (k - 1) blank lines.
    const empties = blankParagraphCount(run.length - 1)
    if (empties === 0) return run
    return "\n\n" + `${sentinel}\n\n`.repeat(empties)
  })
}
